import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { EAccessLevel } from "../../../../shared/oversight-core/enums/access-level";
import { IAccessGrant } from "../../../../shared/oversight-core/interfaces/access-grant";
import { IAccessTarget } from "../../../../shared/oversight-core/interfaces/access-target";
import AppModal from "../../../../shared/oversight-core/ui-elements/app-modal/app-modal";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import MaterialIcon from "../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import {
  accessLevelDescriptions,
  getAccessLevelInfo,
  isExpiryRequired,
} from "../../../../shared/oversight-core/utils/get-org-user-info";
import GrantExpiryPicker from "../grant-expiry-picker/grant-expiry-picker";
import OptionCard from "../option-card/option-card";
import AccessTargetTree from "./access-target-tree/access-target-tree";

interface IProps {
  show: boolean;
  userName: string;
  existingGrants: IAccessGrant[];
  accessTargets: IAccessTarget[];
  isLoadingTargets?: boolean;
  // Levels the user's role may hold; a Monitoring guest gets View redacted only.
  allowedLevels: EAccessLevel[];
  onBack: () => void;
  onSave: (grant: IAccessGrant) => void;
}

const AddAccessModal = (props: IProps) => {
  const {
    show,
    userName,
    existingGrants,
    accessTargets,
    isLoadingTargets,
    allowedLevels,
    onBack,
    onSave,
  } = props;
  const [searchText, setSearchText] = useState("");
  const [target, setTarget] = useState<IAccessTarget>();
  const [level, setLevel] = useState(EAccessLevel.VIEW);
  const [expiresAt, setExpiresAt] = useState<string>();

  const defaultLevel = allowedLevels.includes(EAccessLevel.VIEW)
    ? EAccessLevel.VIEW
    : allowedLevels[0];

  useEffect(() => {
    if (show) {
      setSearchText("");
      setTarget(undefined);
      setLevel(defaultLevel);
      setExpiresAt(undefined);
    }
  }, [show, defaultLevel]);

  const grantedLevels = useMemo(
    () => new Map(existingGrants.map((g) => [g.identity, g.level])),
    [existingGrants],
  );

  const expiryRequired = isExpiryRequired(level);
  const canSave = !!target && (!expiryRequired || !!expiresAt);

  return (
    <AppModal show={show} size="modal-lg">
      <>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <div className="text-dark font-size-24 font-weight-600">
              Add access
            </div>
            <div className="d-flex align-items-center gap-1 text-light font-size-13 mt-1">
              <MaterialIcon icon="person_outline" size={16} />
              For {userName}
            </div>
          </div>
          <MaterialIcon
            icon="close"
            className="cursor-pointer"
            onClick={onBack}
          />
        </div>

        <div className="text-dark font-size-14 font-weight-600 mt-4 mb-2">
          1. Where
        </div>
        <AppInput
          placeholder="Search branches and sites"
          icon="search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          errors={{}}
          className="mb-2"
        />
        {isLoadingTargets ? (
          <div className="container-dash text-center text-light font-size-12">
            Loading branches and sites...
          </div>
        ) : (
          <AccessTargetTree
            targets={accessTargets}
            grantedLevels={grantedLevels}
            searchText={searchText}
            selectedId={target?.identity}
            onSelect={setTarget}
          />
        )}

        <div className="text-dark font-size-14 font-weight-600 mt-4 mb-2">
          2. Level
        </div>
        <Row role="radiogroup" className="g-2">
          {allowedLevels.map((l) => (
            <Col key={l} className="col-6 col-md-3">
              <OptionCard
                title={getAccessLevelInfo(l).label}
                description={accessLevelDescriptions[l]}
                selected={level === l}
                onClick={() => setLevel(l)}
              />
            </Col>
          ))}
        </Row>

        <div className="font-size-14 mt-4 mb-2">
          <span className="text-dark font-weight-600">3. Expires</span>
          {expiryRequired && (
            <span className="text-danger font-size-13">
              {" "}
              · required for {getAccessLevelInfo(level).label}
            </span>
          )}
        </div>
        <div className="d-flex flex-wrap align-items-center gap-3">
          <GrantExpiryPicker
            expiresAt={expiresAt}
            onChange={setExpiresAt}
            allowNever={!expiryRequired}
          />
          <span className="text-light font-size-12">
            {expiresAt
              ? "Access stops on this date. The grant stays listed as Expired until removed."
              : "Access lasts until someone removes it."}
          </span>
        </div>

        <Row className="align-items-center g-3 mt-4">
          <Col className="col-auto">
            <AppButton
              text="Save"
              size="medium"
              className="px-5"
              disabled={!canSave}
              onClick={() => target && onSave({ ...target, level, expiresAt })}
            />
          </Col>
          <Col className="col-auto">
            <AppButton
              text="Back"
              variant="transparentWithBorder"
              size="medium"
              className="px-5"
              onClick={onBack}
            />
          </Col>
          {!target && (
            <Col className="text-light font-size-12">
              Choose a branch or site to continue.
            </Col>
          )}
        </Row>
      </>
    </AppModal>
  );
};

export default AddAccessModal;
